import { Link } from "react-router-dom";
import { PageContainer } from "@/components/layout/PageContainer";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDate } from "@satyrsmc/shared/lib/date-utils";
import { useGalleriesList } from "@/queries/event-gallery";

/** Placeholder heading and cover tiles, shaped like the grid they stand in for. */
function GalleriesIndexSkeleton() {
  return (
    <PageContainer>
      <div data-testid="galleries-index-skeleton" className="space-y-6">
        <Skeleton className="h-8 w-40" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].map((n) => (
            <div key={n} className="space-y-2">
              <Skeleton className="aspect-[4/3] w-full rounded-lg" />
              <Skeleton className="h-5 w-2/3" />
            </div>
          ))}
        </div>
      </div>
    </PageContainer>
  );
}

/**
 * The members' gallery index: one card per gallery that has at least one
 * approved photo, newest event first. Open to any signed-in user — gallery
 * viewing is deliberately broader than membership.
 */
export function GalleriesIndexPage() {
  const { data, isLoading, error } = useGalleriesList();

  if (isLoading) return <GalleriesIndexSkeleton />;

  if (error) {
    return (
      <PageContainer>
        <h1 className="text-2xl font-bold">Gallery</h1>
        <p className="text-muted-foreground">{error.message}</p>
      </PageContainer>
    );
  }

  const galleries = data ?? [];

  return (
    <PageContainer>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Gallery</h1>

        {galleries.length === 0 ? (
          <p className="text-muted-foreground">
            No galleries yet — photos from past events will appear here.
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {galleries.map((gallery) => (
              <Link
                key={gallery.id}
                to={`/gallery/${gallery.id}`}
                className="group overflow-hidden rounded-lg border bg-card text-card-foreground shadow-sm transition-colors hover:bg-accent"
              >
                <div className="aspect-[4/3] w-full overflow-hidden bg-muted">
                  <img
                    src={gallery.cover_url}
                    alt=""
                    loading="lazy"
                    className="size-full object-cover transition-transform group-hover:scale-105"
                  />
                </div>
                <div className="space-y-1 p-4">
                  <h2 className="font-semibold leading-tight">{gallery.title}</h2>
                  <p className="text-sm text-muted-foreground">
                    {gallery.photo_count === 1 ? "1 photo" : `${gallery.photo_count} photos`}
                    {gallery.event?.start_date ? ` · ${formatDate(gallery.event.start_date)}` : ""}
                    {gallery.event?.start_date &&
                    gallery.event.end_date &&
                    gallery.event.end_date !== gallery.event.start_date
                      ? ` – ${formatDate(gallery.event.end_date)}`
                      : ""}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </PageContainer>
  );
}
